import React from 'react';
import { Icon } from '@iconify/react';
import {
  computeNextRunAt,
  formatLastRunLabel,
  formatNextRunLabel,
  formatRecipientNames,
  formatRecipientsPhrase,
  formatSchedulePhrase,
  formatCommerciauxLundiFiltersPhrase,
  formatMediaPlanningFiltersPhrase,
  getCronTypeUi,
  hasCronTestMode,
  instantCronTriggerLabel,
  isInstantCronType,
  isInstantRecipientsCronType,
  isSystemCronType,
} from '../../utils/kkpCronUi';

const KkpCronAlertCard = ({
  cron,
  cronTypes = [],
  commerciaux = [],
  showOwner = false,
  showType = false,
  saving = false,
  variant = 'manage',
  onEdit,
  onDuplicate,
  onToggleActive,
  onSend,
  onDelete,
}) => {
  const isTest = hasCronTestMode(cron.cron_type) && Boolean(cron.filters?.test_mode);
  const isSystem = isSystemCronType(cron.cron_type);
  const isInstant = Boolean(cron.instant) || isInstantCronType(cron.cron_type);
  const hasChosenRecipients = Boolean(cron.chooses_recipients) || isInstantRecipientsCronType(cron.cron_type);
  const ui = getCronTypeUi(cron.cron_type);
  const typeLabel = cron.cron_type_label
    || cronTypes.find((type) => type.id === cron.cron_type)?.label
    || cron.cron_type;
  const recipientNames = formatRecipientNames(cron, commerciaux);
  const schedule = formatSchedulePhrase({
    cronType: cron.cron_type,
    weekdays: cron.weekdays,
    weekdayLabel: cron.weekday_label,
    hour: cron.hour,
    minute: cron.minute,
    monthDays: cron.filters?.month_days,
    timeLabel: cron.time_label,
    weekInterval: cron.filters?.week_interval,
    weekAnchor: cron.filters?.week_anchor,
  });
  const nextRun = computeNextRunAt({
    cronType: cron.cron_type,
    weekdays: cron.weekdays,
    hour: cron.hour,
    minute: cron.minute,
    monthDays: cron.filters?.month_days,
    weekInterval: cron.filters?.week_interval,
    weekAnchor: cron.filters?.week_anchor,
  });
  const isDuplicate = variant === 'duplicate';

  return (
    <article className={`kkp-crons-card ${cron.active ? '' : 'is-paused'}`}>
      <div className="kkp-crons-card__top">
        <div className="kkp-crons-card__badges">
          {cron.active ? (
            <span className="kkp-crons-badge kkp-crons-badge--on">Envoi automatique</span>
          ) : (
            <span className="kkp-crons-badge kkp-crons-badge--off">En pause</span>
          )}
          {isTest ? (
            <span className="kkp-crons-badge kkp-crons-badge--test">Essai uniquement</span>
          ) : null}
        </div>
        <h3 className="kkp-crons-card__title">{cron.name}</h3>
      </div>

      <dl className="kkp-crons-card__facts">
        {showType ? (
          <div>
            <dt>Type de mail</dt>
            <dd>
              <span className="kkp-crons-card__type" style={{ color: ui.color }}>
                <Icon icon={ui.icon} width={16} />
                {typeLabel}
              </span>
            </dd>
          </div>
        ) : null}
        {isInstant ? (
          <div>
            <dt>Déclencheur</dt>
            <dd>{instantCronTriggerLabel(cron.cron_type)}</dd>
          </div>
        ) : (
          <div>
            <dt>Date d’envoi</dt>
            <dd>{schedule}</dd>
          </div>
        )}
        {isInstant && !hasChosenRecipients ? (
          <div>
            <dt>Destinataire</dt>
            <dd>Commercial de la ligne</dd>
          </div>
        ) : isSystem ? null : (
          <div>
            <dt>Destinataire</dt>
            <dd>{formatRecipientsPhrase(recipientNames)}</dd>
          </div>
        )}
        {cron.cron_type === 'commerciaux_lundi' ? (
          <div>
            <dt>Dossiers</dt>
            <dd>{formatCommerciauxLundiFiltersPhrase(cron.filters)}</dd>
          </div>
        ) : null}
        {cron.cron_type === 'media_planning' ? (
          <div>
            <dt>Dossiers</dt>
            <dd>{formatMediaPlanningFiltersPhrase(cron.filters)}</dd>
          </div>
        ) : null}
        {isInstant ? null : (
          <>
            <div>
              <dt>{cron.active ? 'Prochain envoi' : 'Reprendrait le'}</dt>
              <dd>{formatNextRunLabel(nextRun)}</dd>
            </div>
            <div>
              <dt>Dernier envoi</dt>
              <dd>{formatLastRunLabel(cron.last_run_at)}</dd>
            </div>
          </>
        )}
        {showOwner && cron.created_by_label ? (
          <div>
            <dt>Créée par</dt>
            <dd>{cron.created_by_label}</dd>
          </div>
        ) : null}
      </dl>

      <div className="kkp-crons-card__actions">
        {isDuplicate ? (
          <button
            type="button"
            className="btn btn-primary-600 kkp-crons-icon-btn"
            onClick={() => onDuplicate?.(cron)}
            title="Dupliquer"
            aria-label="Dupliquer"
          >
            <Icon icon="mdi:content-copy" width={20} />
          </button>
        ) : isInstant ? (
          <>
            {hasChosenRecipients ? (
              <button
                type="button"
                className="btn btn-primary-600 kkp-crons-icon-btn"
                disabled={saving}
                onClick={() => onEdit(cron)}
                title="Choisir les destinataires"
                aria-label="Choisir les destinataires"
              >
                <Icon icon="mdi:pencil-outline" width={20} />
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-outline-secondary kkp-crons-icon-btn"
              disabled={saving}
              onClick={() => onToggleActive(cron)}
              title={cron.active ? 'Mettre en pause' : 'Remettre en route'}
              aria-label={cron.active ? 'Mettre en pause' : 'Remettre en route'}
            >
              <Icon icon={cron.active ? 'mdi:pause' : 'mdi:play'} width={20} />
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-primary-600 kkp-crons-icon-btn"
              disabled={saving}
              onClick={() => onEdit(cron)}
              title="Modifier"
              aria-label="Modifier"
            >
              <Icon icon="mdi:pencil-outline" width={20} />
            </button>
            {isSystem ? null : (
              <button
                type="button"
                className="btn btn-outline-primary kkp-crons-icon-btn"
                disabled={saving}
                onClick={() => onDuplicate(cron)}
                title="Dupliquer"
                aria-label="Dupliquer"
              >
                <Icon icon="mdi:content-copy" width={20} />
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-secondary kkp-crons-icon-btn"
              disabled={saving}
              onClick={() => onSend(cron)}
              title={isSystem ? 'Lancer maintenant' : 'Envoyer maintenant'}
              aria-label={isSystem ? 'Lancer maintenant' : 'Envoyer maintenant'}
            >
              <Icon icon={isSystem ? 'mdi:play-outline' : 'mdi:email-fast-outline'} width={20} />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary kkp-crons-icon-btn"
              disabled={saving}
              onClick={() => onToggleActive(cron)}
              title={cron.active ? 'Mettre en pause' : 'Remettre en route'}
              aria-label={cron.active ? 'Mettre en pause' : 'Remettre en route'}
            >
              <Icon icon={cron.active ? 'mdi:pause' : 'mdi:play'} width={20} />
            </button>
            {isSystem ? null : (
              <button
                type="button"
                className="btn btn-outline-danger kkp-crons-icon-btn"
                disabled={saving}
                onClick={() => onDelete(cron)}
                title="Supprimer"
                aria-label="Supprimer"
              >
                <Icon icon="mdi:delete-outline" width={20} />
              </button>
            )}
          </>
        )}
      </div>
    </article>
  );
};

export default KkpCronAlertCard;
